"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Maximize2, Ruler, Building2, Truck, ArrowUpRight, FileText, CircleCheck as CheckCircle2, Compass } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Requirements() {
  const shouldReduceMotion = useReducedMotion();
  const initialAnim = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const viewAnim = {
    opacity: 1,
    y: 0
  };
  const planningChecklist = [{
    title: "Inflatable Arena Footprint",
    detail: "The inflatable safety cushion defines the true working boundary of the unit, extending well past the steel motion chassis."
  }, {
    title: "Perimeter Access & Queuing",
    detail: "Operational buffers around the arena boundary are required for safe spectator flow, queue control, and operator visibility."
  }, {
    title: "Operator Console Clearance",
    detail: "The control podium must sit outside the fall zone with an unobstructed direct line of sight to the rider and arena exit."
  }, {
    title: "Air Blower Duct Path",
    detail: "Inflation tubes require dedicated unobstructed floor runs from the blower fan to the arena intake sleeves."
  }];
  return <section id="mechanical-bull-space-requirements" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial={initialAnim} whileInView={viewAnim} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 border border-border bg-muted px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-4">
            <Ruler className="size-3.5 text-primary" />
            <span>Facility Planning • Space Requirements</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif uppercase tracking-tight text-foreground leading-tight">
            How Much Space Does a Mechanical Bull Need?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground font-sans leading-relaxed">
            Mechanical bull space requirements vary according to the machine and the inflatable safety arena supplied with it. The bull base alone does not represent the full operating footprint.
          </p>
          <p className="mt-3 text-base text-muted-foreground font-sans leading-relaxed">
            When comparing machines, look for the documented dimensions of the complete setup. Include the inflatable, access around the equipment and any additional space required by the product instructions.
          </p>
        </motion.div>

        {/* Primary Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Venue Context Cards */}
          <motion.div initial={initialAnim} whileInView={viewAnim} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col gap-6">
            {/* Permanent Venue Card */}
            <div className="border border-border bg-card p-6 sm:p-8 relative">
              {/* Corner Registration Markings */}
              <div className="absolute top-1 left-1 size-2 border-t-2 border-l-2 border-foreground" />
              <div className="absolute top-1 right-1 size-2 border-t-2 border-r-2 border-foreground" />
              <div className="absolute bottom-1 left-1 size-2 border-b-2 border-l-2 border-foreground" />
              <div className="absolute bottom-1 right-1 size-2 border-b-2 border-r-2 border-foreground" />

              <div className="flex items-center gap-3 border-b border-border pb-4 mb-5">
                <div className="p-2 border border-border bg-muted">
                  <Building2 className="size-5 text-primary" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                    Fixed Installations
                  </span>
                  <h3 className="text-xl font-bold font-serif uppercase tracking-wide text-foreground">
                    Permanent Venue Planning
                  </h3>
                </div>
              </div>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                For a permanent venue, measure the intended operating location. Factor in structural columns, lighting rigs, fire exit pathways, and HVAC ducts that may impinge on the machine perimeter or upper clear zone.
              </p>

              <div className="mt-6 pt-5 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-border bg-muted p-3.5">
                  <span className="block text-xs font-mono uppercase text-muted-foreground">
                    Subfloor Assessment
                  </span>
                  <span className="text-xs font-sans text-foreground font-medium mt-1 block">
                    Verify level load-bearing floors capable of supporting dynamic motion weight.
                  </span>
                </div>
                <div className="border border-border bg-muted p-3.5">
                  <span className="block text-xs font-mono uppercase text-muted-foreground">
                    Clear Overhead Zone
                  </span>
                  <span className="text-xs font-sans text-foreground font-medium mt-1 block">
                    Account for overhead lighting, signs, ceiling fans, and sprinkler drop clearance.
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile & Event Operations Card */}
            <div className="border border-border bg-card p-6 sm:p-8 relative">
              <div className="absolute top-1 left-1 size-2 border-t-2 border-l-2 border-foreground" />
              <div className="absolute top-1 right-1 size-2 border-t-2 border-r-2 border-foreground" />
              <div className="absolute bottom-1 left-1 size-2 border-b-2 border-l-2 border-foreground" />
              <div className="absolute bottom-1 right-1 size-2 border-b-2 border-r-2 border-foreground" />

              <div className="flex items-center gap-3 border-b border-border pb-4 mb-5">
                <div className="p-2 border border-border bg-muted">
                  <Truck className="size-5 text-primary" />
                </div>
                <div>
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                    Portable Deployments
                  </span>
                  <h3 className="text-xl font-bold font-serif uppercase tracking-wide text-foreground">
                    Mobile Equipment Logistics
                  </h3>
                </div>
              </div>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                For mobile equipment, also consider the packed dimensions and the space required during setup and teardown. Ensure entry corridors, double doors, service ramps, and transit paths accommodate the packed machine frame and rolled inflatable canvas.
              </p>

              <div className="mt-6 pt-5 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-border bg-muted p-3.5">
                  <span className="block text-xs font-mono uppercase text-muted-foreground">
                    Transit Clearance
                  </span>
                  <span className="text-xs font-sans text-foreground font-medium mt-1 block">
                    Check standard doorway widths, elevator thresholds, and tight turn radiuses along transport routes.
                  </span>
                </div>
                <div className="border border-border bg-muted p-3.5">
                  <span className="block text-xs font-mono uppercase text-muted-foreground">
                    Staging Area
                  </span>
                  <span className="text-xs font-sans text-foreground font-medium mt-1 block">
                    Provide staging floor area to safely unroll, orient, and inspect the inflatable mattress.
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Complete Footprint Specification Plate */}
          <motion.div initial={initialAnim} whileInView={viewAnim} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col gap-6">
            {/* Complete Setup Dimensions Guide Card */}
            <div className="border border-border bg-card p-6 sm:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
                <div>
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                    Component Breakdown
                  </span>
                  <h3 className="text-xl font-bold font-serif uppercase tracking-wide text-foreground">
                    Documented Setup Evaluation
                  </h3>
                </div>
                <Compass className="size-5 text-muted-foreground" />
              </div>

              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Every commercial model features distinct engineering specifications. When evaluating manufacturers and product listings, verify that your site plan accounts for each operational perimeter element:
              </p>

              <div className="space-y-4">
                {planningChecklist.map((item, index) => <div key={item.title} data-index={index} className="p-4 border border-border bg-muted flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold font-serif uppercase tracking-wide text-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 p-4 border border-border bg-background flex items-start gap-3">
                <Maximize2 className="size-4 text-primary shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                  Always consult the specific technical documentation and manufacturer spec sheets for the exact dimensions, weight distribution, and perimeter clearances of your chosen bull and arena model.
                </p>
              </div>
            </div>

            {/* Bottom Consultation & Action Card */}
            <div className="border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-5">
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                  Technical Support
                </span>
                <h4 className="text-base font-bold font-serif uppercase tracking-wide text-foreground mt-0.5">
                  Need Help Assessing Your Floor Plan?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Send your room dimensions or layout sketches for equipment fitment verification.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                <Button asChild variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background text-xs font-serif uppercase tracking-wider h-10 px-4">
                  <Link to="/ContactPage">
                    <span>Contact Sales</span>
                    <ArrowUpRight className="size-3.5 ml-1" />
                  </Link>
                </Button>
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-serif uppercase tracking-wider h-10 px-4">
                  <Link to="/SetupSafetyAndMaintenancePage">
                    <FileText className="size-3.5 mr-1" />
                    <span>Setup Guidelines</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}